import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import customPropTypes from 'apps/web_forms/utils/customPropTypes';
import PlusIcon from 'apps/web_forms/assets/plus.svg';
import { COLORS, FormTypes } from 'apps/web_forms/utils/constants';
import Group from 'apps/web_forms/components/Form/Group';
import useResourceData from 'apps/web_forms/hooks/useResourceData';
import useRules from 'apps/web_forms/hooks/useRules';
import ErrorMessage from 'apps/web_forms/components/commons/ErrorMessage';
import { updateFieldErrors } from 'apps/web_forms/slices/formSlice';

import { FormInputsContext } from 'apps/web_forms/contexts/FormInputsContext';
import {
  selectFieldErrorById,
  selectSelectedForm,
} from 'apps/web_forms/hooks/useGlobalSelector';

const StyledRepeatGroupContainer = styled.div`
  &.inactive {
    display: none;
  }
  > h4 {
    font-weight: 300;
    font-size: 0.95em;
    margin-bottom: 0.5em;
  }
  .add-group-btn {
    border: none;
    background-color: transparent;
    display: flex;
    align-items: center;
    font-size: 0.85em;
    font-weight: 300;
    width: 50%;
    svg {
      margin-right: 0.5em;
      background-color: ${COLORS.grey};
      fill: white;
      width: 1.75em;
      height: 1.75em;
      position: relative;
      top: 0.1em;
      padding: 0.5em;
      border-radius: 50%;
      font-size: 18px;
      box-sizing: border-box;
    }
  }
  .error-message {
    color: ${COLORS.red};
  }
`;

const RepeatGroup = ({
  element,
  formContext,
  dispatchInitialAnswers,
  onChangeValue,
  initialLoad = true,
  rerenderVirtualizedRows
}) => {
  const [groups, setGroups] = useState([]);
  const [isLoad, setIsLoad] = useState(initialLoad);
  const currentGroupId = useRef(0);
  const {
    identifier,
    title,
    hint,
    dataBindingResource: dataBindingResourceIdentifer,
    dataBindingTable: dataBindingTableId,
  } = element;
  const isSubmission = window.location.href.includes('submission')
  const [dispatchCreated, setDispatchCreated] = useState(false)
  const [areInitialAnswersChecked, setAreInitialAnswersChecked] = useState(false)

  const { updateGroupElementExpressions } = useContext(FormInputsContext);

  const errorMessage = useSelector((state) => selectFieldErrorById(state, identifier));

  const {
    resourceData,
    isLoading: loadingResource,
    qualifiedResourceId,
  } = useResourceData(dataBindingResourceIdentifer);

  const [{ isActive, isRequired, initialAnswer }] = useRules(element, formContext);

  const selectedForm = useSelector(selectSelectedForm);
  const isDispatch = selectedForm?.type === FormTypes.DISPATCH;
  const isDispatchDraft = selectedForm?.type === FormTypes.DISPATCH_DRAFT;

  const dispatch = useDispatch();

  //TODO: Check if this useEffect is necessary
  useEffect(() => {
    return () => {
      onChangeValue(identifier);
    };
  }, [groups]);

  useEffect(
    function validateGroup(){
      let newErrorMessage;
      if (!loadingResource && isRequired && groups.length === 0 && isActive) {
        newErrorMessage = 'This field is required';
      }

      let updateErrorsFormSlice = {}
      updateErrorsFormSlice[identifier] = newErrorMessage;

      dispatch(
        updateFieldErrors({
          updatedFieldErrors: updateErrorsFormSlice
        })
      );
    },
    [isRequired, loadingResource, groups.length]
  )

  useEffect(() => {
    async function createDataBindingGroups() {
      if (!resourceData || !dataBindingTableId) return;

      const dataBindingTable = resourceData.find(
        (table) => table.table_id === dataBindingTableId
      );

      if (!dataBindingTable) return;

      // Each column contains databindings for an unique field
      // Each row contains the data to fill an unique group

      // Find the column with max rows
      const rowCount = dataBindingTable.columns.reduce(
        (maxRows, { rows }) => (maxRows < rows.length ? rows.length : maxRows),
        0
      );

      // Iterate over the rows to create groups with their respective bound data
      // The data is injected into the fields through an initialAnswer
      for (let i = 0; i < rowCount; i++) {
        // Each child is a field of the group
        const boundChildren = element.children.map((childElement) => {
          const currentColumn = dataBindingTable.columns.find(
            (column) => column.column_id === childElement.dataBindingColumn
          );

          // Create new child with its bound data
          if (currentColumn && currentColumn.rows[i]) {
            return {
              ...childElement,
              initialAnswer: currentColumn.rows[i]?.row_value,
            };
          }

          // If the field doesn't have data binding data, we just use the original child
          return childElement;
        });

        // We create the new group with its new children
        addGroup({ ...element, children: boundChildren });
        // dispatch(markResourceLoaded(qualifiedResourceId));
      }
    }

    if (qualifiedResourceId) {
      createDataBindingGroups();
    }
  }, [loadingResource]);

  // If this RepeatGroup has bound data from a dispatch, the following code will create
  // the groups and populate their data
  useEffect(() => {
    if (dispatchInitialAnswers) {
      // we are on a draft
      if (window.location.href.includes('origin=draft')) {
        return;
      }

      if (groups.length === 0 && !dispatchCreated) {
        dispatchInitialAnswers.forEach((initialAnswerList) => {
          if (Object.keys(initialAnswerList).length) {
            const boundChildren = bindDispatchInitialAnswersToChildren(
              element.children,
              initialAnswerList
            );

            addGroup({ ...element, children: boundChildren });
          }
        });
        setDispatchCreated(true)
      }
    }
  }, [element, groups.length]);

  // If a Repeat Group has initial asnwer is only because has draft asnwers.
  // There's no regular initial asnwers on repeat groups
  useEffect(() => {
    
    if (initialAnswer) {
      initialAnswer.forEach((_group) => {
        // Each element here is a group to create
        if(!groups || groups.length === 0){
          addGroup();
        }
        setAreInitialAnswersChecked(true)
      });
    }
  }, [initialAnswer]);

  const addGroup = useCallback(
    async (parentElement = element) => {
      currentGroupId.current += 1;

      const suffix = `${currentGroupId.current}`;
      const newGroup = {
        groupId: suffix,
        groupElements: parentElement.children.map((groupElement) => {
          const newElementIdentifier = `${groupElement.identifier.replace(
            parentElement.identifier,
            `${parentElement.identifier}|${suffix}`
          )}`;

          let groupElementWithSuffix;

          if (dispatchInitialAnswers) {
            const initialValue = groupElement.hasInitialAnswer
              ? groupElement.initialAnswer
              : '';
              if(initialValue) {
                groupElementWithSuffix = {
                  ...groupElement,
                  identifier: newElementIdentifier,
                  initialAnswer: initialValue,
                };
              } else {
                groupElementWithSuffix = {
                  ...groupElement,
                  identifier: newElementIdentifier,
                };
              }
          } else {
            groupElementWithSuffix = {
              ...groupElement,
              identifier: newElementIdentifier,
            };
          }

          if (groupElementWithSuffix.children) {
            const assignNewIdentifierToSubForm = (group) => {
              const newGroupArray = group.map((innerGroupElement) => {
                const innerGroupElementWithSuffix = { ...innerGroupElement };

                innerGroupElementWithSuffix.identifier = `${innerGroupElement.identifier.replace(
                  parentElement.identifier,
                  `${parentElement.identifier}|${suffix}`
                )}`;

                if (innerGroupElementWithSuffix.children) {
                  innerGroupElementWithSuffix.children =
                    assignNewIdentifierToSubForm(
                      innerGroupElementWithSuffix.children
                    );
                }
                return innerGroupElementWithSuffix;
              });
              return newGroupArray;
            };
            groupElementWithSuffix.children = assignNewIdentifierToSubForm(
              groupElementWithSuffix.children
            );
          }

          // this will run only when new groups are manually added
          // it will clear the initialAnswer key completely so the new groups will not have their fields filled
          if (!isLoad && groups.length && dispatchInitialAnswers) {
            let itemsWithoutValues = removeKeyFromObject(
              groupElementWithSuffix?.children,
              'initialAnswer'
            );

            groupElementWithSuffix = {
              ...groupElementWithSuffix,
              children: itemsWithoutValues ?? null,
            };
          }

          // this will run when a repeater group is loaded
          // it will check if the repeater group has dispatched values
          // if will remove initial answers of elements that don't have a dispatched initial answer
          if (
            groupElementWithSuffix.dispatchInitialAnswers &&
            groupElementWithSuffix.dispatchInitialAnswers.length
          ) {
            groupElementWithSuffix.dispatchInitialAnswers.forEach(
              (initialAnswer) => {
                if (Object.keys(initialAnswer).length === 0) {
                  const itemsWithoutInitialAnswers = removeKeyFromObject(
                    groupElementWithSuffix?.children,
                    'initialAnswer'
                  );
                  groupElementWithSuffix = {
                    ...groupElementWithSuffix,
                    children: itemsWithoutInitialAnswers ?? null,
                  };
                }
              }
            );
          }

          // DME-2763
          if((isDispatch || isDispatchDraft || isSubmission) && (dispatchCreated || areInitialAnswersChecked)) {
            groupElementWithSuffix = {
              ...groupElementWithSuffix,
              initialAnswer: ''
            };
          }

          return groupElementWithSuffix;
        }),
      };
      
      setIsLoad(false);
      setGroups((prevGroups) => [...prevGroups, newGroup]);
      
      // ******************************************************
      // ******************************************************
      // THIS TIMEOUT IS USEFUL BECAUSE IT WAITS UNTIL trigger() OF REACT-HOOK-FORM IS EXECUTED
      // ******************************************************
      // ******************************************************
      // setTimeout(() => updateGroupElementExpressions(newGroup.groupElements), 0);

    },
    [groups.length]
  );

  // Whenever groups are updated, either when a group is added, or removed evaluate expressions
  useEffect(() => {
    groups.forEach(async (group) => {
      await new Promise((resolve) => setTimeout(resolve, 0));
      updateGroupElementExpressions(group.groupElements);
    });
  }, [groups]);

  const removeKeyFromObject = (obj, keyToRemove) => {
    if (obj) {
      return JSON.parse(
        JSON.stringify(obj, (key, val) => {
          return key === keyToRemove ? undefined : val;
        })
      );
    } else {
      return null;
    }
  }

  const removeGroup = (groupId) => {
    setIsLoad(false);
    setGroups(groups.filter((e) => e.groupId !== groupId));
  };

  return (
    <div
      className={`
        react-form-control
        ${!isActive ? 'inactive' : ''}
      `}
    >
      <StyledRepeatGroupContainer>
        <h4>{title}</h4>
        {groups.map((g) => (
          <Group
            identifier={identifier}
            key={g.groupId}
            groupElements={g.groupElements}
            groupId={g.groupId}
            onDelete={removeGroup}
            formContext={formContext}
            onChangeValue={onChangeValue}
            rerenderVirtualizedRows={rerenderVirtualizedRows}
          />
        ))}

        <button
          type='button'
          aria-label='Add Group'
          onClick={() => addGroup()}
          className='add-group-btn'
          id={`add_more_answers_for_${identifier}`}
        >
          <PlusIcon />
          Add group
        </button>
        {errorMessage !== undefined && (
          <ErrorMessage id={`${identifier}_errmsg`}>
            {errorMessage}
          </ErrorMessage>
        )}
      </StyledRepeatGroupContainer>
      {hint && <p className='element-hint'>{hint}</p>}
      {loadingResource && (
        <p className='element-hint'>Loading groups with prefilled data...</p>
      )}
    </div>
  );
};

function lazyFunction(f) {
  return function () {
    return f.apply(this);
  };
}

export const bindDispatchInitialAnswersToChildren = (children, initialAnswerList) => {
  return children.map((childElement) => {
    const [fieldIdentifier] = childElement.identifier
      .split('/')
      .slice(-1);

    const hasCurrentInitialAnswer = Object.prototype.hasOwnProperty.call(
      initialAnswerList,
      fieldIdentifier
    );
    const currentInitialAnswer = initialAnswerList[fieldIdentifier];

    let boundChild = {
      ...childElement,
      hasInitialAnswer: true,
    };

    if (hasCurrentInitialAnswer) {
      if (currentInitialAnswer instanceof Array) {
        boundChild.dispatchInitialAnswers = currentInitialAnswer;
      } else if (!Object.prototype.hasOwnProperty.call(boundChild, 'initialAnswerExpr')) {
        boundChild.initialAnswer = currentInitialAnswer ?? '';
      } else {
        delete boundChild.initialAnswer;
      }
    } else if (!Object.prototype.hasOwnProperty.call(boundChild, 'initialAnswerExpr')) {
      boundChild.initialAnswer = '';
    } else {
      delete boundChild.initialAnswer;
    }

    return boundChild;
  });
};

const lazyElement = lazyFunction(function () {
  return elementType;
});

const elementType = PropTypes.shape({
  identifier: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  hint: PropTypes.string,
  children: PropTypes.arrayOf(lazyElement),
  dataBindingResource: PropTypes.string,
  dataBindingTable: PropTypes.string,
});

RepeatGroup.propTypes = {
  element: elementType.isRequired,
  formContext: customPropTypes.formContext.isRequired,
  dispatchInitialAnswers: PropTypes.arrayOf(PropTypes.shape({})),
  onChangeValue: PropTypes.func.isRequired,
};

RepeatGroup.defaultProps = {
  dispatchInitialAnswers: undefined,
};

export default React.memo(RepeatGroup);
